import { supabase } from '../lib/supabase';
import type { TradingPartner, TradingPartnerTemplate, TradingPartnerWithTemplate, InjectedTradingPartner } from '../types/tradingPartner';

/**
 * Find a single active trading partner by name (case-insensitive match).
 * Returns the partner with its current template, or null if not found.
 * Used by Mode B (full document re-open) to bypass the TP picker.
 */
export async function findTradingPartnerByName(name: string): Promise<TradingPartnerWithTemplate | null> {
  const { data: partners, error } = await supabase
    .from('trading_partners')
    .select('*')
    .ilike('name', name)
    .eq('is_active', true)
    .limit(1);

  if (error) {
    console.error('Error finding trading partner by name:', error);
    return null;
  }

  if (!partners?.length) return null;

  const partner = partners[0];
  const template = await fetchTemplateForPartner(partner.id);
  return { ...partner, template: template || undefined };
}

export async function fetchActiveTradingPartners(): Promise<TradingPartnerWithTemplate[]> {
  const { data, error } = await supabase
    .from('trading_partners')
    .select('*')
    .eq('is_active', true)
    .order('name')
    .limit(100);

  if (error) {
    console.error('Error fetching trading partners:', error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch active trading partners, filtered by an allowlist from the host app.
 * Matches on trading_partners.code which stores the reports partners.id as string.
 * When injectedTPs is not provided (standalone mode), returns all active TPs.
 */
export async function fetchFilteredTradingPartners(
  injectedTPs?: InjectedTradingPartner[]
): Promise<TradingPartnerWithTemplate[]> {
  const allPartners = await fetchActiveTradingPartners();

  if (!injectedTPs || injectedTPs.length === 0) {
    return allPartners;
  }

  const allowedCodes = new Set(injectedTPs.map((tp) => String(tp.code)));
  return allPartners.filter(
    (partner) => allowedCodes.has(partner.code)
  );
}

/**
 * Fetch the current template assigned to a trading partner via the junction table.
 * Returns null if the partner has no assigned template.
 */
export async function fetchTemplateForPartner(
  tradingPartnerId: string,
  documentType?: string
): Promise<TradingPartnerTemplate | null> {
  // Step 1: Get template IDs assigned to this partner
  const { data: assignments, error: assignError } = await supabase
    .from('tp_template_assignments')
    .select('template_id')
    .eq('trading_partner_id', tradingPartnerId);

  if (assignError) {
    console.error('Error fetching template assignments:', assignError);
    throw assignError;
  }

  if (!assignments || assignments.length === 0) return null;

  const templateIds = assignments.map((a) => a.template_id);

  // Step 2: Fetch the current template from those IDs
  let query = supabase
    .from('trading_partner_templates')
    .select('*')
    .in('id', templateIds)
    .eq('is_current', true);

  if (documentType) {
    query = query.eq('document_type', documentType);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    console.error('Error fetching trading partner template:', error);
    throw error;
  }

  return data;
}

/**
 * Resolve a template using a specific-to-generic fallback chain via the junction table:
 *   1. Partner-assigned + DocType + EDI Version (most specific)
 *   2. Partner-assigned + DocType
 *   3. Unassigned + DocType + EDI Version (generic)
 *   4. Unassigned + DocType only
 *   5. Unassigned + no scope (global default)
 *
 * "Unassigned" = templates with zero rows in tp_template_assignments.
 */
export async function resolveTemplate(
  partnerId: string | null,
  documentType: string,
  ediVersion?: string
): Promise<TradingPartnerTemplate | null> {
  const docTypeCode = documentType === 'asn' ? '856' : documentType === 'invoice' ? '810' : documentType;
  console.log('[resolveTemplate]', { partnerId, documentType, docTypeCode });

  // --- Partner-specific scopes (via junction table) ---
  if (partnerId) {
    // Get template IDs assigned to this partner
    const { data: assignments, error: assignError } = await supabase
      .from('tp_template_assignments')
      .select('template_id')
      .eq('trading_partner_id', partnerId);
    console.log('[resolveTemplate] assignments:', assignments?.length, 'error:', assignError);

    if (assignments && assignments.length > 0) {
      const templateIds = assignments.map((a) => a.template_id);

      // Scope 1: Partner + DocType + Version
      if (ediVersion) {
        const { data } = await supabase
          .from('trading_partner_templates')
          .select('*')
          .in('id', templateIds)
          .eq('is_current', true)
          .eq('document_type', docTypeCode)
          .eq('edi_version', ediVersion)
          .maybeSingle();
        if (data) return data;
      }

      // Scope 2: Partner + DocType
      const { data } = await supabase
        .from('trading_partner_templates')
        .select('*')
        .in('id', templateIds)
        .eq('is_current', true)
        .eq('document_type', docTypeCode)
        .is('edi_version', null)
        .maybeSingle();
      if (data) return data;

      // Also try: Partner + DocType (any version — in case edi_version is set on the template)
      const { data: anyVersion } = await supabase
        .from('trading_partner_templates')
        .select('*')
        .in('id', templateIds)
        .eq('is_current', true)
        .eq('document_type', docTypeCode)
        .maybeSingle();
      if (anyVersion) return anyVersion;
    }
  }

  // --- Generic scopes (templates NOT assigned to any partner) ---
  // Find all template IDs that ARE assigned
  const { data: allAssigned } = await supabase
    .from('tp_template_assignments')
    .select('template_id');
  const assignedIds = (allAssigned || []).map((a) => a.template_id);

  // Helper: query unassigned templates
  const queryUnassigned = (extraFilters: Record<string, string | null>) => {
    let query = supabase
      .from('trading_partner_templates')
      .select('*')
      .eq('is_current', true);

    // Exclude assigned templates
    if (assignedIds.length > 0) {
      // MyRest may not support .not().in() — filter client-side instead
    }

    for (const [key, value] of Object.entries(extraFilters)) {
      if (value === null) {
        query = query.is(key, null);
      } else {
        query = query.eq(key, value);
      }
    }

    return query;
  };

  // Scope 3: Unassigned + DocType + Version
  if (ediVersion) {
    const { data } = await queryUnassigned({ document_type: docTypeCode, edi_version: ediVersion }).maybeSingle();
    if (data && !assignedIds.includes(data.id)) return data;
  }

  // Scope 4: Unassigned + DocType
  {
    const { data } = await queryUnassigned({ document_type: docTypeCode, edi_version: null }).maybeSingle();
    if (data && !assignedIds.includes(data.id)) return data;
  }

  // Scope 5: Unassigned + no scope (global default)
  {
    const { data } = await queryUnassigned({ document_type: null, edi_version: null }).maybeSingle();
    if (data && !assignedIds.includes(data.id)) return data;
  }

  return null;
}

/**
 * Given a template ID (from documents.trading_partner_id), look up the
 * trading partner(s) assigned to it via the junction table.
 * Returns the first assigned TP with the template attached.
 */
export async function fetchTradingPartnerWithTemplate(
  templateId: string
): Promise<TradingPartnerWithTemplate | null> {
  // Fetch the template
  const { data: template, error: templateError } = await supabase
    .from('trading_partner_templates')
    .select('*')
    .eq('id', templateId)
    .maybeSingle();

  if (templateError) {
    console.error('Error fetching template:', templateError);
    throw templateError;
  }
  if (!template) return null;

  // Look up which TP(s) are assigned to this template via junction
  const { data: assignments, error: assignError } = await supabase
    .from('tp_template_assignments')
    .select('trading_partner_id')
    .eq('template_id', templateId)
    .limit(1);

  if (assignError) {
    console.error('Error fetching template assignments:', assignError);
    throw assignError;
  }

  if (!assignments || assignments.length === 0) {
    // Generic template — no TP assigned
    return null;
  }

  // Fetch the trading partner
  const { data: partner, error: partnerError } = await supabase
    .from('trading_partners')
    .select('*')
    .eq('id', assignments[0].trading_partner_id)
    .maybeSingle();

  if (partnerError) {
    console.error('Error fetching trading partner:', partnerError);
    throw partnerError;
  }

  if (!partner) return null;

  return { ...partner, template };
}

// Legacy alias — used by TradingPartnerSelector
export const fetchTradingPartnerTemplate = fetchTemplateForPartner;

export async function createTradingPartner(
  code: string,
  name: string,
  description?: string
): Promise<TradingPartner> {
  const { data, error } = await supabase
    .from('trading_partners')
    .insert({
      code,
      name,
      description,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating trading partner:', error);
    throw error;
  }

  return data;
}

/**
 * Create a template and optionally assign it to a trading partner.
 * If tradingPartnerId is provided, creates a junction row.
 */
export async function createTradingPartnerTemplate(
  templateConfig: Record<string, unknown>,
  tradingPartnerId?: string,
  documentType?: string,
  version: string = '1.0'
): Promise<TradingPartnerTemplate> {
  const { data, error } = await supabase
    .from('trading_partner_templates')
    .insert({
      version,
      is_current: true,
      template_config: templateConfig,
      document_type: documentType,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating trading partner template:', error);
    throw error;
  }

  // Create junction row if a partner is specified
  if (tradingPartnerId) {
    const { error: assignError } = await supabase
      .from('tp_template_assignments')
      .insert({
        trading_partner_id: tradingPartnerId,
        template_id: data.id,
      });

    if (assignError) {
      console.error('Error assigning template to partner:', assignError);
      throw assignError;
    }
  }

  return data;
}
